import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild, HostListener, ElementRef } from '@angular/core';
import { Chart, registerables, ChartData, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ApiSdkService } from '../../services/api-sdk.service';

Chart.register(...registerables);

const TYPE_COLORS: Record<string, string> = {
  prompt: '#f59e0b',
  deploy: '#3b82f6',
  start_server: '#8b5cf6',
  get_source_code: '#6366f1',
  recover_version: '#ec4899',
  upload_file: '#14b8a6',
  add_custom_domain: '#64748b',
  chatgpt: '#10b981',
  image_generation: '#f43f5e',
  email: '#06b6d4',
  pdf: '#84cc16',
  document_scan: '#a855f7',
  web_scraper: '#f97316',
  file_upload: '#78716c',
  create_project: '#0ea5e9',
  other: '#9ca3af',
};

const TYPE_LABELS: Record<string, string> = {
  prompt: 'AI Prompt',
  deploy: 'Deploy',
  start_server: 'Start Server',
  get_source_code: 'Source Code',
  recover_version: 'Recover Version',
  upload_file: 'Upload File',
  add_custom_domain: 'Custom Domain',
  chatgpt: 'ChatGPT',
  image_generation: 'Image Gen',
  email: 'Email',
  pdf: 'PDF',
  document_scan: 'Doc Scan',
  web_scraper: 'Web Scraper',
  file_upload: 'File Upload',
  create_project: 'Create Project',
  other: 'Other',
};

interface DailyData {
  date: string;
  development: number;
  infrastructure: number;
  byType: Record<string, number>;
}

interface AnalyticsData {
  daily: DailyData[];
  totals: { development: number; infrastructure: number; total: number; byType: Record<string, number> };
  projects: string[];
}

type RangeKey = 'today' | 'week' | 'month' | 'custom';
type ViewKey = 'general' | 'development' | 'infrastructure' | 'byType';

@Component({
  selector: 'app-credit-usage-chart',
  templateUrl: './credit-usage-chart.component.html',
  styleUrls: ['./credit-usage-chart.component.scss']
})
export class CreditUsageChartComponent implements OnInit, OnChanges {
  @Input() userData: any;
  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  expanded = false;
  loading = false;

  activeRange: RangeKey = 'month';
  customFrom = '';
  customTo = '';

  activeView: ViewKey = 'general';

  projects: string[] = [];
  selectedProject = '';
  projectDropdownOpen = false;
  projectSearch = '';

  analyticsData: AnalyticsData | null = null;

  chartData: ChartData = { labels: [], datasets: [] };
  chartOptions: ChartOptions = {};
  chartType: ChartType = 'line';

  ranges: { key: RangeKey; label: string }[] = [
    { key: 'today', label: 'TODAY' },
    { key: 'week', label: 'THIS_WEEK' },
    { key: 'month', label: 'THIS_MONTH' },
    { key: 'custom', label: 'CUSTOM_RANGE' },
  ];

  views: { key: ViewKey; label: string }[] = [
    { key: 'general', label: 'GENERAL' },
    { key: 'development', label: 'DEVELOPMENT' },
    { key: 'infrastructure', label: 'INFRASTRUCTURE' },
    { key: 'byType', label: 'BY_TYPE' },
  ];

  constructor(private apiSdkService: ApiSdkService, private elementRef: ElementRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.projectDropdownOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.projectDropdownOpen = false;
      this.projectSearch = '';
    }
  }

  ngOnInit(): void {
    if (this.userData) {
      this.initCustomDates();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userData'] && this.userData) {
      this.initCustomDates();
    }
  }

  private initCustomDates(): void {
    const now = new Date();
    this.customTo = this.formatDate(now);
    const threeMonthsAgo = new Date(now);
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 1);
    this.customFrom = this.formatDate(threeMonthsAgo);
  }

  async toggleExpanded(): Promise<void> {
    this.expanded = !this.expanded;
    if (this.expanded && !this.analyticsData) {
      await this.loadData();
    }
  }

  async onRangeChange(range: RangeKey): Promise<void> {
    this.activeRange = range;
    if (range !== 'custom') {
      await this.loadData();
    }
  }

  async onCustomRangeChange(): Promise<void> {
    if (this.customFrom && this.customTo) {
      await this.loadData();
    }
  }

  onViewChange(view: ViewKey): void {
    this.activeView = view;
    this.buildChart();
  }

  async onProjectChange(): Promise<void> {
    await this.loadData();
  }

  selectProject(project: string): void {
    this.selectedProject = project;
    this.projectDropdownOpen = false;
    this.projectSearch = '';
    this.onProjectChange();
  }

  get filteredProjects(): string[] {
    if (!this.projectSearch) return this.projects;
    const search = this.projectSearch.toLowerCase();
    return this.projects.filter(p => p.toLowerCase().includes(search));
  }

  async loadData(): Promise<void> {
    this.loading = true;
    try {
      const { from, to } = this.getDateRange();
      const result = await this.apiSdkService.getSpendingAnalytics(from, to, this.selectedProject || undefined);
      if (result?.success?.data) {
        this.analyticsData = result.success.data;
        this.projects = this.analyticsData!.projects || [];
      }
      this.buildChart();
    } catch (e) {
      console.error('Error loading spending analytics:', e);
    } finally {
      this.loading = false;
    }
  }

  private getDateRange(): { from: string; to: string } {
    const now = new Date();
    const to = this.formatDate(now);
    let from: string;

    switch (this.activeRange) {
      case 'today':
        from = to;
        break;
      case 'week': {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 6);
        from = this.formatDate(weekAgo);
        break;
      }
      case 'month': {
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        from = this.formatDate(monthStart);
        break;
      }
      case 'custom':
        from = this.customFrom;
        return { from, to: this.customTo };
      default:
        from = to;
    }
    return { from, to };
  }

  private buildChart(): void {
    if (!this.analyticsData) return;

    const daily = this.analyticsData.daily;

    if (daily.length === 0) {
      this.chartData = { labels: [], datasets: [] };
      return;
    }

    const labels = daily.map(d => this.formatLabel(d.date));

    switch (this.activeView) {
      case 'general':
        this.chartType = daily.length <= 1 ? 'bar' : 'line';
        this.chartData = {
          labels,
          datasets: [{
            label: 'Total Credits',
            data: daily.map(d => parseFloat((d.development + d.infrastructure).toFixed(1))),
            borderColor: '#f59e0b',
            backgroundColor: daily.length <= 1 ? '#f59e0b' : 'rgba(245, 158, 11, 0.1)',
            fill: daily.length > 1,
            tension: 0.3,
            pointRadius: daily.length > 14 ? 0 : 4,
            pointHoverRadius: 6,
          }]
        };
        break;

      case 'development':
        this.chartType = daily.length <= 1 ? 'bar' : 'line';
        this.chartData = {
          labels,
          datasets: [{
            label: 'Development',
            data: daily.map(d => d.development),
            borderColor: '#3b82f6',
            backgroundColor: daily.length <= 1 ? '#3b82f6' : 'rgba(59, 130, 246, 0.1)',
            fill: daily.length > 1,
            tension: 0.3,
            pointRadius: daily.length > 14 ? 0 : 4,
            pointHoverRadius: 6,
          }]
        };
        break;

      case 'infrastructure':
        this.chartType = daily.length <= 1 ? 'bar' : 'line';
        this.chartData = {
          labels,
          datasets: [{
            label: 'Infrastructure',
            data: daily.map(d => d.infrastructure),
            borderColor: '#10b981',
            backgroundColor: daily.length <= 1 ? '#10b981' : 'rgba(16, 185, 129, 0.1)',
            fill: daily.length > 1,
            tension: 0.3,
            pointRadius: daily.length > 14 ? 0 : 4,
            pointHoverRadius: 6,
          }]
        };
        break;

      case 'byType':
        this.chartType = 'bar';
        const allTypes = new Set<string>();
        daily.forEach(d => Object.keys(d.byType).forEach(t => allTypes.add(t)));
        const types = Array.from(allTypes).sort();

        this.chartData = {
          labels,
          datasets: types.map(type => ({
            label: TYPE_LABELS[type] || type,
            data: daily.map(d => parseFloat((d.byType[type] || 0).toFixed(1))),
            backgroundColor: TYPE_COLORS[type] || '#9ca3af',
            borderRadius: 3,
          }))
        };
        break;
    }

    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: {
          display: this.activeView === 'byType',
          position: 'bottom',
          labels: { boxWidth: 10, padding: 8, font: { size: 11 } }
        },
        tooltip: {
          backgroundColor: '#1f2937',
          titleFont: { size: 12 },
          bodyFont: { size: 11 },
          padding: 10,
          cornerRadius: 8,
          callbacks: {
            label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y} credits`
          }
        }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { font: { size: 10 }, maxRotation: 0 }
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(0,0,0,0.05)' },
          ticks: { font: { size: 10 } },
          stacked: this.activeView === 'byType',
        }
      },
    };
    if (this.activeView === 'byType') {
      (this.chartOptions.scales as any).x.stacked = true;
    }

    // Force chart update
    setTimeout(() => this.chart?.update(), 0);
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  private formatLabel(dateStr: string): string {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }

  get minDate(): string {
    const d = new Date();
    d.setMonth(d.getMonth() - 3);
    return this.formatDate(d);
  }

  get maxDate(): string {
    return this.formatDate(new Date());
  }

  get hasData(): boolean {
    return !!this.analyticsData && this.analyticsData.daily.length > 0;
  }
}
